import { formatWithCommas } from "common/utils"
import React, { useEffect, useState } from "react"
import { Bar } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import Select from "react-select"

const SalesByStatuses = ({ getTotal, barOptions, estoreDashboardData }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const [ordersData, setOrdersData] = useState({ labels: [], datasets: [] })
  const [amountData, setAmountData] = useState({ labels: [], datasets: [] })
  const [selectedStatus, setSelectedStatus] = useState(null)

  useEffect(() => {
    if (estoreDashboardData?.salesByStatus?.length) {
      const labels = estoreDashboardData.salesByStatus.map(item =>
        t(item.status)
      )

      const totalOrders = estoreDashboardData.salesByStatus.map(
        item => item.totalOrders
      )

      const totalAmount = estoreDashboardData.salesByStatus.map(
        item => item.totalAmount
      )

      setOrdersData({
        labels,
        datasets: [
          {
            label: t("Total Orders"),
            data: totalOrders,
            backgroundColor: "#9AD0F5",
          },
        ],
      })

      setAmountData({
        labels,
        datasets: [
          {
            label: t("Total Amount"),
            data: totalAmount,
            backgroundColor: "#00E396",
          },
        ],
      })
    }
  }, [estoreDashboardData, language])

  return (
    <>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
                <CardTitle className="mb-2 mb-md-0">
                  {t("E-Store Dashboard")}
                </CardTitle>
                {estoreDashboardData?.salesByStatus?.length > 0 && (
                  <Select
                    styles={{ width: "100px" }}
                    value={selectedStatus}
                    isClearable
                    placeholder={t("Filter By Status")}
                    options={estoreDashboardData?.salesByStatus?.map(item => ({
                      value: item.status,
                      label: t(item.status),
                    }))}
                    onChange={option => setSelectedStatus(option)}
                  />
                )}
              </div>

              {estoreDashboardData?.salesByStatus?.length === 0 ? (
                <p className="text-center my-3">{t("No data found")}</p>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>{t("Order Status")}</th>
                        <th>{t("Orders")}</th>
                        <th className="d-none d-sm-table-cell">
                          {t("Order %")}
                        </th>
                        <th>{t("Gross Amount")}</th>
                        <th className="d-none d-md-table-cell">
                          {t("Delivery Charges")}
                        </th>
                        <th>{t("Total Amount")}</th>
                        <th className="d-none d-sm-table-cell">
                          {t("Amount %")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {estoreDashboardData?.salesByStatus
                        ?.filter(item => {
                          if (!selectedStatus) return true
                          if (item.status === selectedStatus?.value) return true
                          return false
                        })
                        .map((item, key) => (
                          <tr key={key}>
                            <td>{t(item?.status)}</td>
                            <td>{formatWithCommas(item?.totalOrders)}</td>
                            <td className="d-none d-sm-table-cell">
                              {formatWithCommas(
                                item?.orderPercentage?.toFixed(2)
                              )}
                            </td>
                            <td>
                              {formatWithCommas(
                                item?.amountWithoutDelivery?.toFixed(2)
                              )}
                            </td>
                            <td className="d-none d-md-table-cell">
                              {formatWithCommas(
                                item?.deliveryCharges?.toFixed(2)
                              )}
                            </td>
                            <td>
                              {formatWithCommas(item?.totalAmount?.toFixed(2))}
                            </td>
                            <td className="d-none d-sm-table-cell">
                              {formatWithCommas(
                                item?.amountPercentage?.toFixed(2)
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                    {!selectedStatus && (
                      <tfoot className="table-light">
                        <tr>
                          <th>{t("Total")}</th>
                          <th>
                            {formatWithCommas(
                              getTotal(
                                estoreDashboardData?.salesByStatus,
                                "totalOrders"
                              )
                            )}
                          </th>
                          <th className="d-none d-sm-table-cell">
                            {formatWithCommas(
                              getTotal(
                                estoreDashboardData?.salesByStatus,
                                "orderPercentage"
                              )?.toFixed(2)
                            )}
                          </th>
                          <th>
                            {formatWithCommas(
                              getTotal(
                                estoreDashboardData?.salesByStatus,
                                "amountWithoutDelivery"
                              )?.toFixed(2)
                            )}
                          </th>
                          <th className="d-none d-md-table-cell">
                            {formatWithCommas(
                              getTotal(
                                estoreDashboardData?.salesByStatus,
                                "deliveryCharges"
                              )?.toFixed(2)
                            )}
                          </th>
                          <th>
                            {formatWithCommas(
                              getTotal(
                                estoreDashboardData?.salesByStatus,
                                "totalAmount"
                              )?.toFixed(2)
                            )}
                          </th>
                          <th className="d-none d-sm-table-cell">
                            {formatWithCommas(
                              getTotal(
                                estoreDashboardData?.salesByStatus,
                                "amountPercentage"
                              )?.toFixed(2)
                            )}
                          </th>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <CardTitle>{t("E-Store Order Numbers")}</CardTitle>
              <Bar
                width={550}
                height={180}
                data={ordersData}
                options={barOptions}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <CardTitle>{t("E-Store Order Amount")}</CardTitle>
              <Bar
                width={550}
                height={180}
                data={amountData}
                options={barOptions}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default SalesByStatuses
